import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { PhotoSlideProps } from '../types';

interface IndicatorProps {
  active: boolean;
}

interface NavButtonProps {
  left?: boolean;
  right?: boolean;
}

const PhotoSlide: React.FC<PhotoSlideProps> = ({ 
    images, 
    autoPlay = true, 
    showDots = true, 
    showArrows = true,
    swapInterval = 3000
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalImageIndex, setModalImageIndex] = useState<number>(0);

  // Auto-change slideshow every swapInterval
  useEffect(() => {
    if (autoPlay && images && images.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => 
          (prevIndex + 1) % images.length
        );
      }, swapInterval); 

      return () => clearInterval(interval);
    }
  }, [images, autoPlay, swapInterval]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setIsModalOpen(false);
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const openModal = (index: number): void => {
    setModalImageIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = (): void => {
    setIsModalOpen(false);
  };

  const nextImage = (): void => {
    if (!images || images.length === 0) return;
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const prevImage = (): void => {
    if (!images || images.length === 0) return;
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const nextModalImage = (): void => {
    setModalImageIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const prevModalImage = (): void => {
    setModalImageIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  return (
    <>
      <div>
        
        {images && images.length > 0 && (
          <SlideshowContainer>
            {/* Main hero image */}
            <HeroImage>
              <img
                src={`${images[currentImageIndex]}`}
                alt={`Image ${currentImageIndex + 1}`}
                onClick={() => openModal(currentImageIndex)}
              />
            </HeroImage>
            
            {showDots && images.length > 1 && (
              <SlideshowIndicators>
                {images.map((_, index) => (
                  <Indicator 
                    key={index} 
                    active={index === currentImageIndex}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                ))}
              </SlideshowIndicators>
            )}

            {showArrows && images.length > 1 && (
              <>
                <NavButton left onClick={prevImage} aria-label="Previous image">&#8249;</NavButton>
                <NavButton right onClick={nextImage} aria-label="Next image">&#8250;</NavButton>
              </>
            )}
          </SlideshowContainer>
        )}
      </div>

      {/* Image Modal via Portal to avoid clipping/flicker from transformed ancestors */}
      {isModalOpen && images && (
        ReactDOM.createPortal(
          <Modal onClick={closeModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <CloseButton onClick={closeModal}>&times;</CloseButton>
              {images.length > 1 && (
                <>
                  <NavButton left onClick={prevModalImage}>&#8249;</NavButton>
                  <NavButton right onClick={nextModalImage}>&#8250;</NavButton>
                </>
              )}
              <ModalImage>
                <img 
                  src={`${images[modalImageIndex]}`} 
                  alt={`Image ${modalImageIndex + 1}`}
                />
              </ModalImage>
              {images.length > 1 && (
                <ModalIndicators>
                  {images.map((_, index) => (
                    <ModalIndicator 
                      key={index} 
                      active={index === modalImageIndex}
                      onClick={() => setModalImageIndex(index)}
                    />
                  ))}
                </ModalIndicators>
              )}
            </ModalContent>
          </Modal>,
          document.body
        )
      )}
    </>
  );
};

export const ProductContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem 4rem;
`;

const SlideshowContainer = styled.div`
  position: relative;
  width: 100%;
  margin-top: 1rem;
`;

const HeroImage = styled.div`
  height: 200px; /* Fixed container height */
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    transition: opacity 0.3s ease-in-out;
    cursor: pointer;
    object-fit: contain; /* Maintain aspect ratio */
    
    &:hover {
      opacity: 0.9;
      transform: scale(1.02);
      transition: all 0.3s ease;
    }
  }

  @media (max-width: 768px) {
    height: 300px; /* Smaller height for mobile */
  }
`;

const SlideshowIndicators = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 1rem;
`;

const Indicator = styled.button<IndicatorProps>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.active ? 'var(--primary-color)' : 'rgba(0, 0, 0, 0.3)'};
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-color)' : 'rgba(0, 0, 0, 0.5)'};
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100000;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 95vw;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -50px;
  right: 0;
  font-size: 40px;
  color: white;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 1001;
  padding: 5px;
  
  &:hover {
    opacity: 0.7;
  }
`;

const NavButton = styled.button<NavButtonProps>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.left ? 'left: -60px;' : 'right: -60px;'}
  font-size: 60px;
  color: white;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 1001;
  padding: 10px;
  
  &:hover {
    opacity: 0.7;
  }

  @media (max-width: 768px) {
    ${props => props.left ? 'left: 10px;' : 'right: 10px;'}
    font-size: 40px;
  }
`;

const ModalImage = styled.div`
  img {
    max-width: 100%;
    max-height: 80vh;
    object-fit: contain;
    border-radius: 8px;
  }
`;

const ModalIndicators = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
`;

const ModalIndicator = styled.button<IndicatorProps>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.8)'};
  }
`;

export default PhotoSlide; 